/**
 * Workers, and the first orders in the game that take longer than a turn.
 *
 * Everything before this resolved immediately or moved a unit one tile. An improvement
 * takes `turnsToBuild` worker-turns and the progress lives on the order, which means
 * two workers on the same tile finish in half the time — the only reason to ever build
 * a second one, and a decision rather than a formality.
 *
 * Improvements are the first thing that makes the *land* worth something beyond what
 * it was generated as. A hillside is worth more this century than last because someone
 * dug a mine into it.
 */

import type { Content, ImprovementDef } from '../../../content/schema/index.js';
import { hasTech } from '../research/knowledge.js';
import type { GameEvent } from '../commands/types.js';
import type { GameState, Id, TileGrid, Unit } from '../state/types.js';
import { allUnits, unitDef, unitIndex } from './index.js';

export function canBuildImprovements(content: Content, unit: Unit): boolean {
  return unitDef(content, unit).abilities.includes('build_improvement');
}

/** Whether an improvement may be built on a tile at all. */
export function improvementAllowed(content: Content, map: TileGrid, index: number, improvement: ImprovementDef): boolean {
  if (improvement.key === 'none') return false;
  const terrain = content.terrain.get(map.terrain[index]!);
  if (terrain.water || !terrain.passable) return false;
  if (!improvement.validTerrain.includes(terrain.key)) return false;
  // Already there — nothing to do. A road is its own layer; anything else fills the one slot.
  if (improvement.route) return map.road[index] === 0;
  return map.improvement[index] !== improvement.id;
}

/** Improvements this worker could start where it stands, in content order. */
export function buildableHere(content: Content, state: GameState, unit: Unit): ImprovementDef[] {
  const index = unitIndex(state.map, unit);
  const civ = state.civs[unit.owner];
  return content.improvements.all.filter(
    (improvement) => improvementAllowed(content, state.map, index, improvement) && (!civ || hasTech(civ, improvement.techId)),
  );
}

/**
 * Everyone working on the same improvement on the same tile.
 *
 * Progress is shared rather than per-unit: the tile is what is being improved, not the
 * worker. Capped so that stacking twenty workers is not a way to make a mine appear
 * instantly.
 */
function crewOn(state: GameState, content: Content, index: number, improvement: Id, owner: Id): Unit[] {
  const crew = allUnits(state).filter(
    (unit) =>
      unit.owner === owner &&
      unit.orders?.kind === 'build' &&
      unit.orders.improvement === improvement &&
      unitIndex(state.map, unit) === index,
  );
  return crew.slice(0, content.rules.work.maxWorkersPerTile);
}

/**
 * Advance every worker's build order by one turn.
 *
 * Called once per turn for the whole world rather than per unit, because a crew shares
 * its progress and stepping each member separately would count the same turn twice.
 */
export function advanceWork(state: GameState, content: Content): GameEvent[] {
  const events: GameEvent[] = [];
  const done = new Set<string>();

  for (const unit of allUnits(state)) {
    const order = unit.orders;
    if (!order || order.kind !== 'build') continue;

    const index = unitIndex(state.map, unit);
    const key = `${index}:${order.improvement}:${unit.owner}`;
    if (done.has(key)) continue;
    done.add(key);

    const improvement = content.improvements.get(order.improvement);
    if (!improvementAllowed(content, state.map, index, improvement)) {
      // The ground changed under them — someone else built here first.
      for (const member of crewOn(state, content, index, order.improvement, unit.owner)) member.orders = null;
      events.push({ t: 'WorkAbandoned', unitId: unit.id, at: index });
      continue;
    }

    const crew = crewOn(state, content, index, order.improvement, unit.owner);
    const progress = order.progress + crew.length;

    if (progress >= improvement.turnsToBuild) {
      if (improvement.route) state.map.road[index] = 1;
      else state.map.improvement[index] = improvement.id;
      for (const member of crew) member.orders = null;
      events.push({ t: 'ImprovementBuilt', at: index, name: improvement.name, civId: unit.owner });
    } else {
      // One shared figure, written to every member, so the panel agrees with itself
      // whichever worker the player happens to have selected.
      for (const member of crew) {
        if (member.orders?.kind === 'build') member.orders.progress = progress;
      }
    }
  }

  return events;
}

/** Turns left for a worker's current job, or null when it is not building. */
export function turnsRemaining(content: Content, state: GameState, unit: Unit): number | null {
  const order = unit.orders;
  if (!order || order.kind !== 'build') return null;
  const improvement = content.improvements.get(order.improvement);
  const crew = crewOn(state, content, unitIndex(state.map, unit), order.improvement, unit.owner).length;
  return Math.max(1, Math.ceil((improvement.turnsToBuild - order.progress) / Math.max(1, crew)));
}
